<template lang="pug">
el-container.has-centered-content
    .panel-container
        .logo
            progressive-img(:src="require('@/assets/CObyVA.svg')")
        el-form.panel-form.el-form--label-left(
            ref="form"
            :model="form"
            :rules="rules"
            @submit.native.prevent
        )
            .card.z-depth-2(v-loading="loading")
                .title-container
                    h3(v-if="type === 'signin'") {{ $t('Login') }}
                    h3(v-else-if="type === 'forgot'") {{ $t('Forgot password') }}
                el-form-item(prop="email" :label="$t('Email')" size="large")
                    el-input(
                        autofocus
                        v-model="form.email"
                        @keyup.enter.native="confirmEmail"
                        suffix-icon="el-icon-fa-at fas"
                        ref="email"
                    )
                el-form-item(v-if="type === 'signin'" prop="password" :label="$t('Password')" size="large")
                    el-input(
                        :type="showPassword ? 'text' : 'password'"
                        v-model="form.password"
                        @keyup.enter.native="confirmPassword"
                        ref="password"
                    )
                        i.el-input__icon(
                            slot="suffix"
                            :class="showPassword ? 'el-icon-fa-eye-slash fas' : 'el-icon-fa-eye fas'"
                            @click="showPassword = !showPassword"
                        )
            el-form-item.buttons(v-if="type === 'signin'")
                el-button.password-reset-button(
                    type="info"
                    size="large"
                    :disabled="loading"
                    @click="type = 'forgot'"
                )
                    | {{ $t('Forgot password') }}
                el-button.submit-button(
                    type="primary"
                    size="large"
                    :disabled="loading"
                    @click.native="submit"
                )
                    | {{ $t('Login') }}
            el-form-item.buttons(v-else-if="type === 'forgot'")
                el-button.password-reset-button(
                    type="info"
                    size="large"
                    :disabled="loading"
                    @click="type = 'signin'"
                )
                    | {{ $t('Login') }}
                el-button.submit-button(
                    type="primary"
                    size="large"
                    :disabled="loading"
                    @click.native="submit"
                )
                    | {{ $t('Send me an email with the password reset link') }}
</template>

<i18n>
{
  "de": {
    "Oops, an error occurred!": "Es ist ein Fehler aufgetreten!",
    "You have entered an invalid email or password!": "Ihre Zugangsdaten sind nicht korrekt!",
    "Login": "Login",
    "Forgot password": "Passwort vergessen",
    "Thanks, you will receive an email soon.": "Vielen Dank, Sie werden in Kürze eine Email erhalten.",
    "Send me an email with the password reset link": "E-Mail mit Reset-Link zuschicken",
    "Email": "E-Mail",
    "Password": "Passwort"
  }
}
</i18n>

<script>

import pinia from '@/pinia'
import router from '@/router'
import { useAuthStore, useSetupStore } from '@/stores'

export default {
    name: 'SignIn',
    data() {
        let data = {
            type: 'signin', // possible values: signin / forgot / register
            form: {
                email: '',
                password: '',
            },
            loading: false,
            showPassword: false,
        };

        if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_LOGIN && process.env.VUE_APP_PASSWORD) {
            data.form.email = `${process.env.VUE_APP_LOGIN}`
            data.form.password = `${process.env.VUE_APP_PASSWORD}`
        }

        return data;
    },
    watch: {
        type() {
            setTimeout(() => {
                this.$refs.form.resetFields()
            }, 0)
        }
    },
    computed: {
        rules() {
            let rules = {
                email: 'required|email',
            }

            if (this.type === 'signin') {
                rules.password = 'required'
            }

            return rules
        }
    },
    methods: {
        confirmEmail() {
            // TODO: validate

            if (this.type === 'signin' && this.form.email && ! this.form.password) this.$nextTick(() => this.$refs.password.focus())
            else if (this.type === 'forgot' || (this.form.email && this.form.password)) this.submit()
        },
        confirmPassword() {
            if (! this.form.email) this.$nextTick(() => this.$refs.email.focus())
            else if (this.form.password) this.submit()
        },
        submit() {
            // eslint-disable-next-line
            this.$refs.form.validate((valid, errors) => {
                if (valid) {
                    switch (this.type) {
                        case 'signin':
                            this.signin()
                            break;

                        case 'forgot':
                            this.forgot()
                            break;
                    }
                }
            })
        },
        signin() {
            const authStore = useAuthStore()
            const setupStore = useSetupStore(pinia)
            // this.loading = true
            setupStore.updateLoadedState(false)
            authStore.signin({ email: this.form.email, password: this.form.password })
                .then(() => {
                    setupStore.fetchSetup()
                        .then(() => {
                            // Redirect to stored path after successful login
                            const redirectPath = authStore.redirectAfterLogin
                            if (redirectPath && redirectPath !== '/' && redirectPath !== '/dashboard') {
                                authStore.clearRedirectAfterLogin()
                                router.push(redirectPath)
                            }
                        })
                        .catch(err => {
                            // eslint-disable-next-line
                            console.error(err)
                            this.$message.error(this.$t('Oops, an error occurred!'));
                            throw err
                        })
                        .finally(() => {
                            // this.loading = false
                            setupStore.updateLoadedState(true)
                        })
                })
                .catch(err => {
                    // this.loading = false
                    setupStore.updateLoadedState(true)
                    this.$message.error(this.$t('You have entered an invalid email or password!'));
                    throw err
                })
        },
        forgot() {
            const authStore = useAuthStore()
            this.loading = true
            try {
                authStore.forgotpassword(this.form.email)
                this.$message({
                    duration: 0,
                    showClose: true,
                    message: this.$t('Thanks, you will receive an email soon.')
                });
                this.type = 'signin'
            } catch (err) {
                this.$message.error(this.$t('Oops, an error occurred!'));
                throw err
            } finally {
                this.loading = false
            }
        }
    },
}
</script>

<style lang="sass" scoped>
.el-input__icon.el-icon-fa-eye,
.el-input__icon.el-icon-fa-eye-slash
    cursor: pointer
</style>
